import { Injectable, inject } from '@angular/core';
import { Observable, of, from, throwError, timer } from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  mergeMap,
  concatMap,
  reduce,
  retry,
} from 'rxjs/operators';
import {
  PurchaseReturnOrderItem,
  PurchaseReturnOrderListResponse,
  AvailablePO,
  SKUFromGRN,
  PurchaseReturnOrderAddFormData,
  PurchaseReturnOrderDetailItem,
} from './purchase-return-order.config';

export type VendorSkuMapItem = {
  vendorSkuCode: string;
  vendorSkuName: string;
};
import { SearchFormData } from '@shared/components/search-form/search-form.component';
import { VendorControllerService } from '@app/api/vendor-service/api/vendorController.service';
import { PageInfoVendorVO } from '@app/api/vendor-service/model/pageInfoVendorVO';
import { VendorVO } from '@app/api/vendor-service/model/vendorVO';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  WarehouseQueryResVO,
} from '@app/api/stock-service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { PurchaseOrderControllerService } from '@app/api/purchase-service/api/purchaseOrderController.service';
import {
  PageInfoPurchaseOrderResultDTO,
  PurchaseOrderResultDTO,
} from '@app/api/purchase-service/model/models';
import {
  GoodsStockControllerService,
  InventoryStatusControllerService,
  StockQueryReqDTO,
  GoodsStockResVO,
  InventoryStatusSummaryQueryDTO,
  InventoryStatusSummaryVO,
} from '@app/api/stock-service';
import { PurchaseOrderReturnControllerService } from '@app/api/purchase-service/api/purchaseOrderReturnController.service';
import { PageInfoPurchaseOrderReturnPageItemVO } from '@app/api/purchase-service/model/pageInfoPurchaseOrderReturnPageItemVO';
import { PurchaseOrderReturnPageItemVO } from '@app/api/purchase-service/model/purchaseOrderReturnPageItemVO';
import { PurchaseOrderReturnDTO } from '@app/api/purchase-service/model/purchaseOrderReturnDTO';
import { PurchaseOrderReturnItemDTO } from '@app/api/purchase-service/model/purchaseOrderReturnItemDTO';
import { PurchaseOrderReturnUpdateDTO } from '@app/api/purchase-service/model/purchaseOrderReturnUpdateDTO';
import { formatDateToISO } from '@shared/utils/date-formatter.util';
import { VendorSkuControllerService } from '@app/api/vendor-service/api/vendorSkuController.service';
import { convertTaxAmountToRate } from './por.helper';

@Injectable({
  providedIn: 'root',
})
export class PurchaseReturnOrderService {
  private vendorController = inject(VendorControllerService);
  private warehouseControllerService = inject(WarehouseControllerService);
  private purchaseOrderController = inject(PurchaseOrderControllerService);
  private goodsStockController = inject(GoodsStockControllerService);
  private inventoryStatusController = inject(InventoryStatusControllerService);
  private purchaseOrderReturnController = inject(PurchaseOrderReturnControllerService);
  private vendorSkuController = inject(VendorSkuControllerService);

  /**
   * Helper method to handle blob response conversion
   */
  private handleBlobResponse<T>(response: any): Observable<T> {
    if (BlobConverterUtil.isBlob(response)) {
      return from(BlobConverterUtil.convertToJson(response));
    }
    return of(response);
  }

  getPORList(
    params?: SearchFormData & { 
      page?: number; 
      size?: number;
      sortBy?: string;
      ascending?: boolean;
    }
  ): Observable<PurchaseReturnOrderListResponse> {
    // Prepare API params
    const code = params?.['porNo'] || params?.['rtvNo'] || params?.['code'] || undefined;
    const vendorCode = params?.['vendorCode'] || undefined;
    const warehouseCode = params?.['warehouseCode'] || undefined;
    const poCode = params?.['poNo'] || undefined;
    // Search form emits a single status string; the OpenAPI client expects an array and
    // calls status.forEach(...) synchronously when building query params. Passing a string
    // throws before the Observable is returned and leaves the list stuck in loading (RBA-5040).
    type PorStatus =
      | 'PENDING'
      | 'APPROVED'
      | 'REJECTED'
      | 'IN_PROGRESS'
      | 'PARTIALLY_SHIPPED'
      | 'FULLY_SHIPPED'
      | 'RECEIVED_BY_VENDOR'
      | 'COMPLETED'
      | 'CANCELLED';
    const rawStatus = params?.['status'];
    const status: PorStatus[] | undefined =
      rawStatus == null || rawStatus === ''
        ? undefined
        : Array.isArray(rawStatus)
          ? (rawStatus as PorStatus[])
          : [String(rawStatus) as PorStatus];
    
    // Handle date range
    let startCreationDate: string | undefined = undefined;
    let endCreationDate: string | undefined = undefined;
    if (params?.['beginDate']) {
      startCreationDate = formatDateToISO(params['beginDate'], false);
    }
    if (params?.['endDate']) {
      endCreationDate = formatDateToISO(params['endDate'], true);
    }
    
    const page = params?.page ?? 0;
    const size = params?.size ?? 20;
    const sortBy = params?.sortBy || 'creationDate';
    const ascending = params?.ascending ?? false;

    // Call real API
    return this.purchaseOrderReturnController
      .page2(
        code,
        vendorCode,
        warehouseCode,
        poCode,
        status,
        startCreationDate,
        endCreationDate,
        page,
        size,
        sortBy,
        ascending,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<PageInfoPurchaseOrderReturnPageItemVO>(response)),
        map((apiResponse: PageInfoPurchaseOrderReturnPageItemVO) => {
          // Extract page content and total elements
          const pageContent = BlobConverterUtil.extractPageContent(apiResponse, []) as PurchaseOrderReturnPageItemVO[];
          const totalElements = BlobConverterUtil.extractTotalElements(apiResponse, 0);

          // Map API response to component format
          const mappedData: PurchaseReturnOrderItem[] = (pageContent || []).map(
            (item: PurchaseOrderReturnPageItemVO) => {
              let mappedStatus = item.status || 'PENDING';

              return {
                id: item.id || 0,
                porNo: item.code || '', // Map API code to porNo
                rtvNo: item.code || '', // Keep for backward compatibility
                poCode: item.poCode || '',
                vendorCode: item.vendorCode || '',
                vendorName: item.vendorName || '',
                warehouseCode: item.warehouseCode || '',
                warehouseName: item.warehouseName || '',
                returnQty: 0, // TODO: Calculate from items if available
                returnAmount: 0, // TODO: Calculate from items if available
                reason: item.reason || '',
                status: mappedStatus,
                creationDate: item.creationDate || '',
                returnDate: item.lastUpdateDate || undefined,
              };
            }
          );

          return {
            data: mappedData,
            totalRecords: totalElements,
            loading: false,
          };
        }),
        catchError((error) => {
          console.error('Error loading POR list:', error);
          return of({
            data: [],
            totalRecords: 0,
            loading: false,
          });
        })
      );
  }

  getPORDetail(id: number): Observable<PurchaseReturnOrderItem> {
    // Call real API: /v1/purchase-order-returns/{id}
    return this.purchaseOrderReturnController
      .getDetail2(id, 'body', false, { httpHeaderAccept: 'application/json' as any })
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<any>(response)),
        map((apiItem: any) => {
          // Map API response to component format
          // API returns PurchaseOrderReturnResultDTO which may have different structure
          // Map status from API enum to component format
          let mappedStatus = apiItem.status || 'PENDING';
          // Calculate returnQty and returnAmount from items if available
          let returnQty = 0;
          let returnAmount = 0;
          if (apiItem.items && Array.isArray(apiItem.items)) {
            returnQty = apiItem.items.reduce((sum: number, item: any) => sum + (item.plannedQty || 0), 0);
            returnAmount = apiItem.items.reduce((sum: number, item: any) => {
              const price = item.price || 0;
              const qty = item.plannedQty || 0;
              const tax = item.tax || 0;
              const itemAmount = price * qty * (1 + tax); // Include tax in amount
              return sum + itemAmount;
            }, 0);
          }

          return {
            id: apiItem.id || id,
            porNo: apiItem.code || '', // Map API code to porNo
            rtvNo: apiItem.code || '', // Keep for backward compatibility
            poCode: apiItem.poCode || '',
            poId: apiItem.poId || null, // Store poId if available from API
            vendorCode: apiItem.vendorCode || '',
            vendorName: apiItem.vendorName || '',
            warehouseCode: apiItem.warehouseCode || '',
            warehouseName: apiItem.warehouseName || '',
            returnQty: returnQty,
            returnAmount: returnAmount,
            reason: apiItem.reason || '',
            status: mappedStatus,
            creationDate: apiItem.creationDate || '',
            returnDate: apiItem.lastUpdateDate || undefined,
          };
        }),
        catchError((error) => {
          console.error('Error loading POR detail:', error);
          return throwError(() => error);
        })
      );
  }

  // Get POR detail with items for view component
  getPORDetailWithItems(id: number): Observable<{ por: PurchaseReturnOrderItem; items: PurchaseReturnOrderDetailItem[] }> {
    // Call real API: /v1/purchase-order-returns/{id}
    return this.purchaseOrderReturnController
      .getDetail2(id, 'body', false, { httpHeaderAccept: 'application/json' as any })
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<any>(response)),
        map((apiItem: any) => {
          // Map status from API enum to component format
          let mappedStatus = apiItem.status || 'PENDING';

          // Calculate returnQty and returnAmount from items
          let returnQty = 0;
          let returnAmount = 0;
          const detailItems: PurchaseReturnOrderDetailItem[] = [];
          
          if (apiItem.items && Array.isArray(apiItem.items)) {
            apiItem.items.forEach((item: any, index: number) => {
              const qty = item.plannedQty || 0;
              const price = item.price || 0;
              const tax = item.tax || 0; // Tax as rate (e.g., 0.08)
              const itemAmount = price * qty * (1 + tax);
              
              returnQty += qty;
              returnAmount += itemAmount;
              
              // Map item to detail format
              // API only returns: id, porId, skuCode, price, plannedQty, returnedQty, tax, warehouseCode, reason
              // skuName will be loaded from stock API later
              detailItems.push({
                id: `item-${item.id || index}`,
                grnDetailId: item.porId || 0, // Use porId as grnDetailId
                skuCode: item.skuCode || '',
                skuName: item.skuName || '', // Try to get from API if available
                vendorSkuCode: item.vendorSkuCode ?? '',
                vendorSkuName: item.vendorSkuName ?? '',
                spuCode: '', // Not in API response
                spuName: '', // Not in API response
                uom: '', // Not in API response
                qtyReceived: 0, // Not in API response
                qtyReturnedBefore: item.returnedQty || 0, // Use returnedQty from API
                qtyAvailable: 0, // Not in API response
                currentStock: 0, // Not in API response
                returnQty: qty, // plannedQty from API
                price: price,
                returnAmount: itemAmount, // Calculated: price * plannedQty * (1 + tax)
                taxAmount: tax * 100, // Convert rate to percentage for display (0.08 -> 8)
                tax: tax, // Store tax rate (0.08) from API for saving
                reason: item.reason || '',
              });
            });
          }

          const por: PurchaseReturnOrderItem = {
            id: apiItem.id || id,
            porNo: apiItem.code || '', // Map API code to porNo
            rtvNo: apiItem.code || '', // Keep for backward compatibility
            poCode: apiItem.poCode || '',
            poId: apiItem.poId || null, // Store poId if available from API
            vendorId: apiItem.vendorId ?? undefined,
            vendorCode: apiItem.vendorCode || '',
            vendorName: apiItem.vendorName || '',
            warehouseCode: apiItem.warehouseCode || '',
            warehouseName: apiItem.warehouseName || '',
            returnQty: returnQty,
            returnAmount: returnAmount,
            reason: apiItem.reason || '',
            status: mappedStatus,
            creationDate: apiItem.creationDate || '',
            returnDate: apiItem.lastUpdateDate || undefined,
          };

          return { por, items: detailItems };
        }),
        catchError((error) => {
          console.error('Error loading POR detail with items:', error);
          return throwError(() => error);
        })
      );
  }

  // Get available PO after selecting Vendor + Warehouse
  getAvailablePOs(params?: {
    vendorId?: number;
    warehouseId?: number;
  }): Observable<AvailablePO[]> {
    // Call API to get PO with status=DELIVERED, deleteFlag=false, size=100
    return this.purchaseOrderController
      .page1(
        undefined, // id
        undefined, // purchaseRequest
        undefined, // series
        undefined, // vendorCode
        undefined, // startPostingDate
        undefined, // endPostingDate
        undefined, // startDeliveryDate
        undefined, // endDeliveryDate
        'DELIVERED', // status
        false, // deleteFlag
        0, // page
        100, // size - no paging
        'creationDate', // sortBy
        false, // ascending
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<PageInfoPurchaseOrderResultDTO>(response)),
        switchMap((response: PageInfoPurchaseOrderResultDTO) => {
          if (!response || !response.pageContent) {
            return of([]);
          }

          // Return PO as-is from API, don't split by warehouse
          const poList: AvailablePO[] = [];

          response.pageContent.forEach((po: PurchaseOrderResultDTO) => {
            if (!po.items || po.items.length === 0) {
              return;
            }

            // Use series (poNo) as primary key
            const poSeries = po.series || '';
            // Check if PO has at least one item with warehouseCode
            const hasWarehouseCode = po.items.some(item => item.warehouseCode);
            if (!hasWarehouseCode) {
              return;
            }

            // Create one PO entry
            poList.push({
              id: po.id || 0,
              poNo: poSeries,
              vendorId: 0, // Will be resolved from vendorCode
              vendorCode: po.vendorCode || po.vendor?.vendorCode || '',
              vendorName: po.vendor?.vendorName || '',
              warehouseId: 0, // Will be set when warehouse is selected
              warehouseCode: '', // Will be set when warehouse is selected
              warehouseName: '', // Will be set when warehouse is selected
              status: po.status || '',
              creationDate: po.creationDate || '',
              deliveryDate: po.deliveryDate || '',
              grnCount: 1, // TODO: Calculate from actual GRN data
            });
          });

          // Load vendor list to map vendorCode to vendorId
          return this.loadVendorsForMapping().pipe(
            map((vendorMap: Map<string, { id: number; code: string; name: string }>) => {
              // Update PO with vendor info only (warehouse will be set when selected)
              const pos = poList.map((po) => {
                const vendor = vendorMap.get(po.vendorCode);
                const mappedPO = {
                  ...po,
                  vendorId: vendor?.id || 0,
                };
                return mappedPO;
              });

              return pos;
            })
          );
        }),
        catchError((error) => {
          console.error('Error loading available POs:', error);
          return of([]);
        })
      );
  }

  // Load vendors for mapping vendorCode to vendorId
  private loadVendorsForMapping(): Observable<Map<string, { id: number; code: string; name: string }>> {
    return this.vendorController
      .page(
        undefined, // id
        undefined, // vendorCode
        undefined, // vendorName
        undefined, // vendorEmail
        undefined, // vendorPhone
        undefined, // vendorAddress
        false, // deleteFlag - only get non-deleted vendors
        undefined, // country
        undefined, // taxNumber
        undefined, // vendorType
        undefined, // billingCurrency
        undefined, // paymentTerm
        undefined, // paymentMethod
        undefined, // bankAccountName
        undefined, // bankAccountNumber
        undefined, // bankName
        undefined, // swiftCode
        undefined, // category
        undefined, // riskLevel
        undefined, // skuCodes
        'ACTIVE', // status - only active vendors
        0, // page
        100, // size - load all
        'vendorName', // sortBy
        true, // ascending
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<PageInfoVendorVO>(response)),
        map((response: PageInfoVendorVO) => {
          const vendors = response.pageContent || [];
          const vendorMap = new Map<string, { id: number; code: string; name: string }>();
          vendors.forEach((vendor: VendorVO) => {
            if (vendor.vendorCode) {
              vendorMap.set(vendor.vendorCode, {
                id: (vendor as any).id || 0, // VendorVO might have id in actual response
                code: vendor.vendorCode,
                name: vendor.vendorName || '',
              });
            }
          });
          return vendorMap;
        }),
        catchError((error) => {
          console.error('Error loading vendors for mapping:', error);
          return of(new Map<string, { id: number; code: string; name: string }>());
        })
      );
  }

  /**
   * Load stock map for PO items (grouped by warehouse).
   * Returns Map<skuCode, Map<warehouseCode, stock>>.
   */
  loadStockMapForPOItems(poDetails: {
    items?: Array<{ warehouseCode?: string; skuCode?: string }>;
  }): Observable<
    Map<string, Map<string, GoodsStockResVO & { availableQuantity: number }>>
  > {
    const items = poDetails.items || [];
    if (items.length === 0) {
      return of(new Map());
    }

    const warehouseCodes: string[] = Array.from(
      new Set(
        items
          .map((item: { warehouseCode?: string }) => item.warehouseCode)
          .filter((code: string | undefined) => code)
      )
    ) as string[];

    if (warehouseCodes.length === 0) {
      return of(new Map());
    }

    const itemsByWarehouse = new Map<
      string,
      Array<{ warehouseCode?: string; skuCode?: string }>
    >();
    items.forEach((item: { warehouseCode?: string; skuCode?: string }) => {
      const warehouseCode = item.warehouseCode;
      if (warehouseCode) {
        if (!itemsByWarehouse.has(warehouseCode)) {
          itemsByWarehouse.set(warehouseCode, []);
        }
        itemsByWarehouse.get(warehouseCode)!.push(item);
      }
    });

    const warehouseRequests = warehouseCodes.map(
      (warehouseCode: string, index: number) => {
        const warehouseItems =
          itemsByWarehouse.get(warehouseCode) || [];
        const skuCodes: string[] = Array.from(
          new Set(
            warehouseItems
              .map((item: { skuCode?: string }) => item.skuCode)
              .filter((code: string | undefined) => code)
          )
        ) as string[];
        return { warehouseCode, skuCodes, index };
      }
    );

    return from(warehouseRequests).pipe(
      concatMap(({ warehouseCode, skuCodes, index }) => {
        const delayMs = index * 200;
        return timer(delayMs).pipe(
          mergeMap(() =>
            this.loadStockBySkuCodes(warehouseCode, skuCodes).pipe(
              retry({
                count: 2,
                delay: () => timer(1000),
              }),
              map((response) => ({
                warehouseCode,
                items: response.items,
              })),
              catchError(() => of({ warehouseCode, items: [] }))
            )
          )
        );
      }),
      reduce(
        (
          acc: Map<
            string,
            Map<string, GoodsStockResVO & { availableQuantity: number }>
          >,
          { warehouseCode, items }
        ) => {
          items.forEach(
            (stockItem: GoodsStockResVO & { availableQuantity: number }) => {
              if (stockItem.skuCode) {
                if (!acc.has(stockItem.skuCode)) {
                  acc.set(stockItem.skuCode, new Map());
                }
                const skuMap = acc.get(stockItem.skuCode)!;
                skuMap.set(warehouseCode, stockItem);
              }
            }
          );
          return acc;
        },
        new Map<
          string,
          Map<string, GoodsStockResVO & { availableQuantity: number }>
        >()
      ),
      catchError(() => of(new Map()))
    );
  }

  // Get PO details by ID
  getPODetails(poId: number): Observable<PurchaseOrderResultDTO> {
    return this.purchaseOrderController.getDetail1(poId).pipe(
      switchMap((response: any) => this.handleBlobResponse<PurchaseOrderResultDTO>(response)),
      map((poDetails: PurchaseOrderResultDTO) => {
        return poDetails;
      }),
      catchError((error) => {
        console.error('Error getting PO details:', error);
        throw error;
      })
    );
  }

  // Get SKU list from GRN of selected PO
  getSKUFromGRN(params: {
    poId: number;
    warehouseId: number;
  }): Observable<SKUFromGRN[]> {
    // TODO: Implement API call
    // Load SKU from GRN of PO at selected warehouse
    // Calculate: qtyReceived, qtyReturnedBefore, qtyAvailable, currentStock
    return of([]);
  }

  createPOR(data: PurchaseReturnOrderAddFormData): Observable<any> {
    // Map component data to API DTO format
    const apiItems: PurchaseOrderReturnItemDTO[] = (data.items || []).map((item: SKUFromGRN) => {
      // Get tax value - could be from taxAmount (percentage) or tax (rate)
      let taxValue = 0;
      if (item.taxAmount !== undefined && item.taxAmount !== null) {
        // taxAmount might be percentage (8) or rate (0.08)
        // If >= 1, it's percentage, convert to rate
        taxValue = item.taxAmount >= 1 ? item.taxAmount / 100 : item.taxAmount;
      } else if (item.tax !== undefined && item.tax !== null) {
        // tax is already a rate
        taxValue = item.tax;
      }

      // Get price - use price field, fallback to unitPrice for backward compatibility
      const itemPrice = item.price || (item as any).unitPrice || 0;
      
      // Ensure price is a positive number
      if (itemPrice <= 0) {
        throw new Error(`Price must be a positive number for SKU: ${item.skuCode}`);
      }

      // Ensure plannedQty is a positive number
      const plannedQty = item.returnQty || 0;
      if (plannedQty <= 0) {
        throw new Error(`Planned quantity must be a positive number for SKU: ${item.skuCode}`);
      }

      return {
        skuCode: item.skuCode || '',
        vendorSkuCode: item.vendorSkuCode || '',
        vendorSkuName: item.vendorSkuName || '',
        price: itemPrice,
        plannedQty: plannedQty,
        tax: taxValue, // Tax as rate (e.g., 0.08)
        reason: item.reason || undefined,
      };
    });

    // Validate reason is not empty
    if (!data.reason || data.reason.trim() === '') {
      return throwError(() => new Error('Reason is required and cannot be empty'));
    }

    const apiDTO: PurchaseOrderReturnDTO = {
      code: data.code || undefined, // Use code from form if provided, otherwise let API generate
      poId: data.poId || undefined,
      warehouseCode: data.warehouseCode || '',
      vendorCode: data.vendorCode || '',
      reason: data.reason.trim(), // Trim whitespace
      items: apiItems,
    };

    // Call real API: POST /v1/purchase-order-returns
    return this.purchaseOrderReturnController
      .addPurchaseOrderReturn(
        apiDTO,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<any>(response)),
        map((result: any) => ({
          success: true,
          message: 'POR created successfully',
          data: result,
        })),
        catchError((error) => {
          console.error('Error creating POR:', error);
          return throwError(() => error);
        })
      );
  }

  updatePOR(id: number, data: any): Observable<any> {
    // If data contains status, use status update API
    if (data.status) {
      // Component already passes correct status, use it directly
      return this.purchaseOrderReturnController
        .updatePurchaseOrderReturnStatus(
          id,
          data.status,
          'body',
          false,
          { httpHeaderAccept: 'application/json' as any }
        )
        .pipe(
          switchMap((response: any) => this.handleBlobResponse<any>(response)),
          map((result: any) => ({
            success: true,
            message: 'POR status updated successfully',
            data: result,
          })),
          catchError((error) => {
            console.error('Error updating POR status:', error);
            return throwError(() => error);
          })
        );
    }
    
    // For other updates, use updatePurchaseOrderReturn method
    // Component already maps data correctly, just pass it through
    const updateDTO: PurchaseOrderReturnUpdateDTO = {
      warehouseCode: data.warehouseCode || '',
      vendorCode: data.vendorCode || '',
      reason: data.reason || '',
      poId: data.poId || undefined, // poId is number, optional
      items: (data.items || [])
        .filter((item: any) => {
          // Filter out items with invalid price or quantity
          const price = Number(item.price || item.unitPrice || 0);
          const plannedQty = Number(item.plannedQty || item.returnQty || 0);
          return price > 0 && plannedQty > 0;
        })
        .map((item: any) => {
          const price = Number(item.price || item.unitPrice || 0);
          const plannedQty = Number(item.plannedQty || item.returnQty || 0);
          const taxAmount = item.taxAmount || 0;
          const tax = taxAmount >= 1 ? taxAmount / 100 : taxAmount; // Convert percentage to rate if needed
          
          return {
            skuCode: item.skuCode || '',
            vendorSkuCode: item.vendorSkuCode ?? '', // Luôn gửi để backend lưu
            vendorSkuName: item.vendorSkuName ?? '', // Luôn gửi để backend lưu (nếu API hỗ trợ)
            price: price > 0 ? price : 0.01, // Ensure positive number
            plannedQty: plannedQty > 0 ? plannedQty : 1, // Ensure positive number
            tax: tax || 0,
            reason: item.reason || '',
          };
        }),
    };

    return this.purchaseOrderReturnController
      .updatePurchaseOrderReturn(
        id,
        updateDTO,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<any>(response)),
        map((result: any) => ({
          success: true,
          message: 'POR updated successfully',
          data: result,
        })),
        catchError((error) => {
          console.error('Error updating POR:', error);
          return throwError(() => error);
        })
      );
  }

  cancelPOR(id: number): Observable<any> {
    // Use status update API with CANCELLED status
    return this.purchaseOrderReturnController
      .updatePurchaseOrderReturnStatus(
        id,
        'CANCELLED',
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<any>(response)),
        map((result: any) => ({
          success: true,
          message: 'POR cancelled successfully',
          data: result,
        })),
        catchError((error) => {
          console.error('Error cancelling POR:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Load stock items for a warehouse (for stock selection dialog)
   * @param warehouseCode Warehouse code
   * @param page Page number (default: 0)
   * @param size Page size (default: 100)
   * @param skuCodes Optional: Filter by SKU codes (e.g., from vendor SKUs)
   */
  loadStock(
    warehouseCode: string,
    page: number = 0,
    size: number = 100,
    skuCodes?: string[]
  ): Observable<{
    items: (GoodsStockResVO & { availableQuantity: number; heldNum: number })[];
    total: number;
  }> {
    if (!warehouseCode) {
      return of({ items: [], total: 0 });
    }

    // If skuCodes is provided but empty, return empty list (vendor has no SKUs)
    if (skuCodes !== undefined && skuCodes.length === 0) {
      return of({ items: [], total: 0 });
    }

    const stockQuery: StockQueryReqDTO = {
      page: page,
      size: size,
      sortBy: 'creationDate',
      ascending: true,
      warehouseCode: warehouseCode,
    };

    // Add SKU codes filter if provided and not empty
    if (skuCodes && skuCodes.length > 0) {
      stockQuery.skuCodes = skuCodes;
    }

    return this.goodsStockController.pageStock(stockQuery).pipe(
      switchMap((response: any) => this.handleBlobResponse<any>(response)),
      map((response: any) => BlobConverterUtil.extractPageContent(response, []) as GoodsStockResVO[]),
      switchMap((pageContent) => this.enrichWithAvailableFromIms(pageContent)),
      map((items) => {
        // Filter out items with availableQuantity = 0
        const filtered = items.filter((item) => item.availableQuantity > 0);
        return { items: filtered, total: filtered.length };
      }),
      catchError((error) => {
        console.error('Error loading stock:', error);
        return of({ items: [], total: 0 });
      })
    );
  }

  /**
   * Sets availableQuantity on each row from IMS's precomputed value (RBA-2315: consumers must
   * not self-calculate available stock). Rows are matched back to the IMS response by (skuCode,
   * warehouseId); a pair with no IMS stock record, or with no batch call made at all (empty
   * input, or the batch call failing), defaults to 0.
   */
  private enrichWithAvailableFromIms(
    items: GoodsStockResVO[]
  ): Observable<(GoodsStockResVO & { availableQuantity: number; heldNum: number })[]> {
    const withZero = () =>
      items.map((item) => ({ ...item, availableQuantity: 0, heldNum: 0 }));

    const pairKey = (skuCode?: string, warehouseId?: number) => `${skuCode}::${warehouseId}`;

    const queryPairs = new Map<string, InventoryStatusSummaryQueryDTO>();
    for (const item of items) {
      if (item.skuCode && item.warehouseId != null) {
        queryPairs.set(pairKey(item.skuCode, item.warehouseId), {
          skuCode: item.skuCode,
          warehouseId: item.warehouseId,
        });
      }
    }

    if (queryPairs.size === 0) {
      return of(withZero());
    }

    return this.inventoryStatusController
      .getInventoryStatusSummaryBatch(Array.from(queryPairs.values()))
      .pipe(
        switchMap((response: any) =>
          this.handleBlobResponse<InventoryStatusSummaryVO[]>(response),
        ),
        map((summaries: InventoryStatusSummaryVO[]) => {
          const summaryByPair = new Map<string, InventoryStatusSummaryVO>();
          for (const summary of summaries ?? []) {
            summaryByPair.set(pairKey(summary.skuCode, summary.warehouseId), summary);
          }
          return items.map((item) => {
            const summary = summaryByPair.get(pairKey(item.skuCode, item.warehouseId));
            return {
              ...item,
              availableQuantity: summary?.available ?? 0,
              heldNum: summary?.held ?? 0,
            };
          });
        }),
        catchError(() => of(withZero()))
      );
  }

  /**
   * Load stock data by warehouse code and SKU codes
   */
  loadStockBySkuCodes(
    warehouseCode: string,
    skuCodes: string[]
  ): Observable<{
    items: (GoodsStockResVO & { availableQuantity: number; heldNum: number })[];
    total: number;
  }> {
    const stockQuery: StockQueryReqDTO = {
      warehouseCode: warehouseCode,
      skuCodes: skuCodes,
      page: 0,
      size: 1000, // Get all matching items
    };

    return this.goodsStockController.pageStock(stockQuery).pipe(
      switchMap((response: any) => this.handleBlobResponse<any>(response)),
      map((response: any) => BlobConverterUtil.extractPageContent(response, []) as GoodsStockResVO[]),
      switchMap((pageContent) => this.enrichWithAvailableFromIms(pageContent)),
      map((items) => {
        // Filter out items with availableQuantity = 0
        const filtered = items.filter((item) => item.availableQuantity > 0);
        return { items: filtered, total: filtered.length };
      }),
      catchError((error) => {
        console.error('Error loading stock by SKU codes:', error);
        return of({ items: [], total: 0 });
      })
    );
  }

  // Load all vendors for dropdown options
  loadVendors(): Observable<Array<{ label: string; value: number; code: string; name: string }>> {
    return this.vendorController
      .page(
        undefined, // id
        undefined, // vendorCode
        undefined, // vendorName
        undefined, // vendorEmail
        undefined, // vendorPhone
        undefined, // vendorAddress
        false, // deleteFlag - only get non-deleted vendors
        undefined, // country
        undefined, // taxNumber
        undefined, // vendorType
        undefined, // billingCurrency
        undefined, // paymentTerm
        undefined, // paymentMethod
        undefined, // bankAccountName
        undefined, // bankAccountNumber
        undefined, // bankName
        undefined, // swiftCode
        undefined, // category
        undefined, // riskLevel
        undefined, // skuCodes
        'ACTIVE', // status - only active vendors
        0, // page
        100, // size - load all
        'vendorName', // sortBy
        true, // ascending
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        map((response: PageInfoVendorVO) => {
          const vendors: VendorVO[] = response.pageContent || [];
          return vendors
            .filter((vendor: VendorVO) => vendor && vendor.vendorCode)
            .map((vendor: VendorVO) => ({
              label: vendor.vendorName || vendor.vendorCode || '',
              value: (vendor as any).id || 0, // VendorVO might have id in actual response
              code: vendor.vendorCode || '',
              name: vendor.vendorName || '',
            }));
        }),
        catchError((error) => {
          console.error('Error loading vendors:', error);
          return of([]);
        })
      );
  }

  // Load all warehouses for dropdown options
  loadWarehouses(): Observable<Array<{ label: string; value: string; code: string; name: string; id?: number }>> {
    const queryDTO: WarehouseQueryReqDTO = {
      page: 0,
      size: 100,
      status: true, // Only active warehouses
    };

    return this.warehouseControllerService.queryWarehousePage(queryDTO).pipe(
      switchMap((response: any) => this.handleBlobResponse<any>(response)),
      map((response: any) => {
        const warehouses: WarehouseQueryResVO[] = response.pageContent || [];
        return warehouses
          .filter(
            (warehouse: WarehouseQueryResVO) =>
              warehouse && warehouse.code && warehouse.name
          )
          .map((warehouse: WarehouseQueryResVO) => ({
            label: `${warehouse.code} - ${warehouse.name}`,
            value: warehouse.code || '', // Use code as value instead of id
            code: warehouse.code || '',
            name: warehouse.name || '',
            id: warehouse.id || 0, // Keep id for reference if needed
          }));
      }),
      catchError((error) => {
        console.error('Error loading warehouses:', error);
        return of([]);
      })
    );
  }

  /**
   * Load vendor SKU codes for a given vendor
   * @param vendorId Vendor ID
   * @returns Observable of SKU codes array
   */
  loadVendorSkus(vendorId: number): Observable<string[]> {
    return this.loadVendorSkuList(vendorId).pipe(
      map((list) => list.map((x) => x.skuCode).filter((c) => c))
    );
  }

  /**
   * Load full vendor SKU list for mapping: skuCode -> vendorSkuCode, vendorProductName
   * @param vendorId Vendor ID
   * @returns Observable of list with skuCode, vendorSkuCode, vendorProductName
   */
  loadVendorSkuList(
    vendorId: number
  ): Observable<Array<{ skuCode: string; vendorSkuCode: string; vendorProductName: string }>> {
    return this.vendorSkuController
      .page1(
        vendorId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true, // active only
        0,
        100,
        undefined,
        undefined
      )
      .pipe(
        switchMap((response: any) => this.handleBlobResponse<any>(response)),
        map((response: any) => {
          const dataArray: any[] =
            response?.pageContent && Array.isArray(response.pageContent)
              ? response.pageContent
              : Array.isArray(response)
                ? response
                : [];
          return dataArray
            .filter((item: any) => item?.skuCode)
            .map((item: any) => ({
              skuCode: item.skuCode,
              vendorSkuCode: item.vendorSkuCode ?? '',
              vendorProductName: item.vendorProductName ?? item.skuName ?? '',
            }));
        }),
        catchError(() => of([]))
      );
  }

  /**
   * Build vendor SKU map and codes from API list.
   */
  buildVendorSkuMapAndCodes(
    list: Array<{ skuCode: string; vendorSkuCode: string; vendorProductName: string }>
  ): { map: Map<string, VendorSkuMapItem>; skuCodes: string[] } {
    const map = new Map<string, VendorSkuMapItem>();
    list.forEach((x) => {
      map.set(x.skuCode, {
        vendorSkuCode: x.vendorSkuCode,
        vendorSkuName: x.vendorProductName,
      });
    });
    const skuCodes = list.map((x) => x.skuCode).filter((c) => c);
    return { map, skuCodes };
  }

  /**
   * Enrich detail items with vendorSkuCode/vendorSkuName from vendor SKU map.
   */
  enrichDetailItemsWithVendorSku(
    detailItemsMap: Map<string, PurchaseReturnOrderDetailItem[]>,
    vendorSkuMap: Map<string, VendorSkuMapItem>
  ): Map<string, PurchaseReturnOrderDetailItem[]> {
    if (vendorSkuMap.size === 0 || detailItemsMap.size === 0) {
      return detailItemsMap;
    }
    const updated = new Map<string, PurchaseReturnOrderDetailItem[]>();
    detailItemsMap.forEach((items, warehouseCode) => {
      const enriched = items.map((item) => {
        const v = vendorSkuMap.get(item.skuCode);
        return {
          ...item,
          vendorSkuCode: v?.vendorSkuCode ?? item.vendorSkuCode ?? '',
          vendorSkuName: v?.vendorSkuName ?? item.vendorSkuName ?? '',
        };
      });
      updated.set(warehouseCode, enriched);
    });
    return updated;
  }

  /**
   * Build create payload from form data and items.
   */
  buildCreatePayload(
    vendorId: number,
    warehouseId: number | null,
    selectedWarehouse: { code: string; name: string },
    selectedVendor: { code: string; name: string },
    reason: string,
    items: PurchaseReturnOrderDetailItem[],
    poId: number | null,
    selectedPO: { poNo?: string } | null,
    porNo = ''
  ): PurchaseReturnOrderAddFormData {
    return {
      vendorId,
      vendorCode: selectedVendor.code,
      vendorName: selectedVendor.name,
      warehouseId: warehouseId!,
      warehouseCode: selectedWarehouse.code,
      warehouseName: selectedWarehouse.name,
      code: porNo,
      poId: poId || null,
      poNo: selectedPO?.poNo || null,
      reason: reason.trim(),
      items: items.map((item) => this.buildItemPayload(item)),
    };
  }

  private buildItemPayload(item: PurchaseReturnOrderDetailItem): SKUFromGRN {
    return {
      grnDetailId: item.grnDetailId || 0,
      skuCode: item.skuCode || '',
      skuName: item.skuName || '',
      vendorSkuCode: item.vendorSkuCode ?? '',
      vendorSkuName: item.vendorSkuName ?? '',
      spuCode: item.spuCode || '',
      spuName: item.spuName || '',
      qtyReceived: item.qtyReceived || 0,
      qtyReturnedBefore: item.qtyReturnedBefore || 0,
      qtyAvailable: item.qtyAvailable || 0,
      currentStock: item.currentStock || 0,
      returnQty: item.returnQty || 0,
      price: item.price || 0,
      taxAmount: item.taxAmount || 0,
      tax: item.tax ?? convertTaxAmountToRate(item.taxAmount || 0),
      reason: item.reason || '',
    };
  }
}

